import React, { useContext, useEffect, useState } from 'react';
import { FirebaseContext } from 'common';
import { ref as databaseRef, onValue, update } from 'firebase/database';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, IconButton, Paper, Snackbar,
  Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import StorefrontIcon from '@mui/icons-material/Storefront';

const FUNCTION_BASE = 'https://europe-west1-allodriver-9139a.cloudfunctions.net';

const STATUS_COLORS = {
  NEW: 'warning',
  REJECTED: 'error'
};

const STATUS_LABELS = {
  NEW: 'Nouvelle',
  APPROVED: 'Approuvée',
  REJECTED: 'Refusée'
};

export default function RestaurantRequests() {
  const { auth, database } = useContext(FirebaseContext);

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(null);   // requestId being processed
  const [toast, setToast] = useState({ open: false, msg: '', severity: 'success' });
  const [rejectDialog, setRejectDialog] = useState({ open: false, id: null, reason: '' });

  useEffect(() => {
    if (!database) return;
    const r = databaseRef(database, 'restaurantAccessRequests');
    const unsub = onValue(r, snap => {
      const val = snap.val() || {};
      const list = Object.entries(val)
        .map(([id, data]) => ({ id, ...data }))
        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      setRequests(list);
      setLoading(false);
    });
    return () => unsub();
  }, [database]);

  const getToken = async () => {
    if (!auth || !auth.currentUser) throw new Error('Non connecté');
    return auth.currentUser.getIdToken(true);
  };

  const handleApprove = async (req) => {
    setApproving(req.id);
    try {
      const token = await getToken();
      const res = await fetch(`${FUNCTION_BASE}/approve_restaurant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ requestId: req.id })
      });
      const json = await res.json();
      if (!res.ok || json.error) throw new Error(json.error || 'Erreur serveur');
      setToast({ open: true, msg: `✅ Compte créé pour ${req.email} — email envoyé !`, severity: 'success' });
    } catch (e) {
      setToast({ open: true, msg: `Erreur : ${e.message}`, severity: 'error' });
    } finally {
      setApproving(null);
    }
  };

  const handleReject = async () => {
    const { id, reason } = rejectDialog;
    setRejectDialog(d => ({ ...d, open: false }));
    try {
      await update(databaseRef(database, `restaurantAccessRequests/${id}`), {
        status: 'REJECTED',
        rejectedAt: Date.now(),
        rejectionReason: reason || ''
      });
      setToast({ open: true, msg: 'Demande refusée.', severity: 'info' });
    } catch (e) {
      setToast({ open: true, msg: `Erreur : ${e.message}`, severity: 'error' });
    }
  };

  const fmt = (ts) => ts ? new Date(ts).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1.5} mb={3}>
        <StorefrontIcon sx={{ color: '#d4af37', fontSize: 28 }} />
        <Typography variant="h5" fontWeight={800}>Demandes restaurateurs</Typography>
        <Chip
          label={`${requests.filter(r => r.status === 'NEW').length} nouvelle(s)`}
          color="warning"
          size="small"
        />
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>
      ) : requests.length === 0 ? (
        <Alert severity="info">Aucune demande pour l'instant.</Alert>
      ) : (
        <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: '#f5f5f5' }}>
                <TableCell><strong>Restaurant</strong></TableCell>
                <TableCell><strong>Responsable</strong></TableCell>
                <TableCell><strong>Email</strong></TableCell>
                <TableCell><strong>Tél.</strong></TableCell>
                <TableCell><strong>Ville</strong></TableCell>
                <TableCell><strong>SIRET</strong></TableCell>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Statut</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map(req => (
                <TableRow
                  key={req.id}
                  sx={{ '&:hover': { background: '#fafafa' }, opacity: req.status !== 'NEW' ? 0.7 : 1 }}
                >
                  <TableCell>
                    <Typography fontWeight={700} fontSize={13}>{req.restaurantName}</Typography>
                    {req.cuisine && <Typography fontSize={11} color="text.secondary">{`${req.cuisine}${req.isHalal && !/halal/i.test(req.cuisine) ? ' ✅ Halal' : ''}`}</Typography>}
                  </TableCell>
                  <TableCell>{req.ownerName}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{req.email}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{req.mobile}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{req.city}</TableCell>
                  <TableCell sx={{ fontSize: 12, fontFamily: 'monospace' }}>{req.siret}</TableCell>
                  <TableCell sx={{ fontSize: 11, whiteSpace: 'nowrap' }}>{fmt(req.createdAt)}</TableCell>
                  <TableCell>
                    <Chip
                      label={STATUS_LABELS[req.status] || req.status}
                      color={req.status === 'APPROVED' ? undefined : (STATUS_COLORS[req.status] || 'default')}
                      sx={req.status === 'APPROVED' ? { bgcolor: '#d4af37', color: '#000', fontWeight: 700 } : undefined}
                      size="small"
                    />
                    {req.uid && (
                      <Typography fontSize={10} color="text.secondary" mt={0.5} sx={{ fontFamily: 'monospace' }}>
                        uid: {req.uid.slice(0, 8)}…
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {req.status === 'NEW' && (
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Approuver — créer le compte + envoyer email">
                          <span>
                            <IconButton
                              size="small"
                              sx={{ color: '#b8941f' }}
                              disabled={approving === req.id}
                              onClick={() => handleApprove(req)}
                            >
                              {approving === req.id
                                ? <CircularProgress size={18} />
                                : <CheckCircleOutlineIcon />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Refuser">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setRejectDialog({ open: true, id: req.id, reason: '' })}
                          >
                            <CancelOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                    {req.status === 'APPROVED' && (
                      <Tooltip title="Renvoyer l'email de bienvenue">
                        <IconButton size="small" color="primary" onClick={() => handleApprove(req)}>
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Message / notes section for selected request */}
      {requests.filter(r => r.message).length > 0 && (
        <Box mt={4}>
          <Typography variant="subtitle1" fontWeight={700} mb={1}>Messages des candidats</Typography>
          <Divider />
          {requests.filter(r => r.message && r.status === 'NEW').map(req => (
            <Paper key={req.id} elevation={1} sx={{ p: 2, mt: 2, borderLeft: '4px solid #EAB308' }}>
              <Typography fontWeight={700} fontSize={13}>{req.restaurantName} — {req.ownerName}</Typography>
              <Typography fontSize={13} color="text.secondary" mt={0.5}>{req.message}</Typography>
            </Paper>
          ))}
        </Box>
      )}

      {/* Reject dialog */}
      <Dialog open={rejectDialog.open} onClose={() => setRejectDialog(d => ({ ...d, open: false }))} maxWidth="xs" fullWidth>
        <DialogTitle>Refuser la demande</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Raison du refus (optionnel)"
            multiline
            minRows={2}
            value={rejectDialog.reason}
            onChange={e => setRejectDialog(d => ({ ...d, reason: e.target.value }))}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(d => ({ ...d, open: false }))}>Annuler</Button>
          <Button onClick={handleReject} color="error" variant="contained">Refuser</Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast(t => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} onClose={() => setToast(t => ({ ...t, open: false }))} sx={{ width: '100%' }}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
